'use client'

import {
  EXTENDED_SANITIZE_OPTIONS,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  PRODUCT_SEE_MORE_LABEL_KEY,
  SHORT_DESCRIPTION_SANITIZE_OPTIONS,
} from '@/feature/product/services/product.constants'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { getPlainText, trimTrailingBr, truncateHtml } from '@/lib/utils/text-utils'
import { parseAndReplaceIframes } from '@/lib/video/video.utils'
import { ReadMoreLink } from '@/ui/read-more-link/ReadMoreLink.client'
import { InnerHtml } from '@/ui/text/inner-html/InnerHtml.client'
import { Title } from '@/ui/text/title/Title'
import { useState } from 'react'
import sanitizeHtml from 'sanitize-html'

const DESCRIPTION_CLASSNAME = 'wysiwyg min-w-full [&>*]:max-w-full [&>*:last-child]:mb-0'
const PREVIEW_CLASSNAME = twMerge(DESCRIPTION_CLASSNAME, 'leading-light text-brand-2-900')

type ProductDescriptionProps = {
  html: string
  /** If true, shows full description with extended HTML support. Default shows truncated preview with title */
  full?: boolean
  /**
   * When provided (and no shortDescription context), uses a navigate anchor link instead of
   * expand/collapse toggle. Only rendered if description exceeds PRODUCT_DESCRIPTION_MAX_LENGTH.
   */
  anchor?: `#${string}`
  /** If true, renders the section h2 title above the preview content */
  showTitle?: boolean
}

export function ProductDescription({
  html,
  full = false,
  anchor,
  showTitle = false,
}: ProductDescriptionProps) {
  const { t } = useTranslation(['productPage', 'generic'])
  const [isExpanded, setIsExpanded] = useState(false)

  if (!html) return null

  const sanitizedHtml = sanitizeHtml(
    html,
    full ? EXTENDED_SANITIZE_OPTIONS : SHORT_DESCRIPTION_SANITIZE_OPTIONS,
  )
  const trimmedHtml = trimTrailingBr(sanitizedHtml)

  // ProductRichDescription
  if (full) {
    // Try to parse and replace iframes with video components
    const parsedElements = parseAndReplaceIframes(trimmedHtml, DESCRIPTION_CLASSNAME, t)

    return (
      <>
        <Title size="2xl" as="h2" className="font-bold">
          {t('productPage:description.title')}
        </Title>
        {parsedElements ? (
          <div className={DESCRIPTION_CLASSNAME}>{parsedElements}</div>
        ) : (
          <InnerHtml html={trimmedHtml} sanitize={false} className={DESCRIPTION_CLASSNAME} />
        )}
      </>
    )
  }

  const plainText = getPlainText(trimmedHtml)
  const needsTruncation = plainText.length > PRODUCT_DESCRIPTION_MAX_LENGTH
  const displayedHtml =
    needsTruncation && !isExpanded
      ? truncateHtml(trimmedHtml, PRODUCT_DESCRIPTION_MAX_LENGTH)
      : trimmedHtml

  return (
    <>
      {showTitle && (
        <Title size="2xl" as="h2" className="font-bold">
          {t('productPage:description.title')}
        </Title>
      )}
      <InnerHtml html={displayedHtml} sanitize={false} className={PREVIEW_CLASSNAME} />
      {needsTruncation && !anchor && (
        <div className="w-fit">
          <ReadMoreLink
            type="expand"
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            expandLabelKey={PRODUCT_SEE_MORE_LABEL_KEY}
            collapseLabelKey="generic:actions.seeLess"
          />
        </div>
      )}
      {anchor && (
        <div className="w-fit">
          <ReadMoreLink
            type="navigate"
            targetId={anchor}
            expandLabelKey={PRODUCT_SEE_MORE_LABEL_KEY}
          />
        </div>
      )}
    </>
  )
}
